// Coach Check-In Audit — identity-grid progress (doc 107, Part A).
//
// Three INDEPENDENT facts are shown per Form coach identity, and none of them may stand in for
// another:
//
//   Identity rule      a durable decision for (franchise, typed name) that applies to every current
//                      and future evaluation under it. Owned by review_state; not derived here.
//   Class audit        how many of the identity's evaluations have a Dash class recorded.
//   Coach attribution  how many of them have a usable reporting coach in the warehouse's last build,
//                      whether by Dash corroboration, an identity rule or (after slice 2b) an
//                      evaluation-level coach choice.
//
// 🔴 A class saved WITHOUT a coach moves Class audit only. Coach attribution is read from the
// warehouse's needs_identity_review, never from the reconciliation table, so a class-only row can
// never read as a resolved coach -- and an evaluation-complete identity never reads as a rule.
//
// The counts arrive from the API, which computes them from one set of SQL expressions shared by the
// grid and the per-evaluation list. This module turns counts into states and filter matches, and
// recomputes the counts from an expanded evaluation list so a save updates its grid row at once.

export const CLASS_AUDIT_STATE = Object.freeze({
  NOT_APPLICABLE: 'not_applicable',
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
});

export const COACH_ATTRIBUTION_STATE = Object.freeze({
  NEEDS_REVIEW: 'needs_coach_review',
  ALL_RESOLVED: 'all_resolved',
  // A stale identity rule whose evaluations have all left the audit relation.
  NO_EVALUATIONS: 'no_evaluations',
});

export const CLASS_AUDIT_STATE_LABEL = Object.freeze({
  [CLASS_AUDIT_STATE.NOT_STARTED]: 'Not started',
  [CLASS_AUDIT_STATE.IN_PROGRESS]: 'In progress',
  [CLASS_AUDIT_STATE.COMPLETE]: 'Complete',
});

// Filter options, in the order the auditor sees them. The first entry of each is NOT necessarily the
// default: the working queue defaults to Coach attribution = Needs coach review.
export const COACH_ATTRIBUTION_FILTERS = Object.freeze([
  { key: 'needs_coach_review', label: 'Needs coach review' },
  { key: 'all_resolved', label: 'All resolved' },
  { key: 'any', label: 'Any' },
]);

export const CLASS_AUDIT_FILTERS = Object.freeze([
  { key: 'any', label: 'Any' },
  { key: 'missing_class', label: 'Missing a class' },
  { key: 'not_started', label: 'Not started' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'complete', label: 'Complete' },
]);

// ─────────────────────────────────────────────────── Evaluation review: the human walk (2026-09-23)
// An evaluation is REVIEWED when an auditor has recorded BOTH judgments -- a class outcome (confirmed,
// unresolved, or no Dash class expected) and a coach outcome (a Dash employee, or the Form's coach
// kept for that evaluation). "No Dash class" + "keep the Form coach" is a completed review, not a
// failed match. The working queue walks THIS, because Coach attribution (the warehouse's view)
// cannot move for any evaluation-level decision until Slice 2b.
export const EVALUATION_REVIEW_STATE = Object.freeze({
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  NO_EVALUATIONS: 'no_evaluations',
});

export const EVALUATION_REVIEW_STATE_LABEL = Object.freeze({
  [EVALUATION_REVIEW_STATE.NOT_STARTED]: 'Not started',
  [EVALUATION_REVIEW_STATE.IN_PROGRESS]: 'In progress',
  [EVALUATION_REVIEW_STATE.COMPLETE]: 'Complete',
});

export const EVALUATION_REVIEW_FILTERS = Object.freeze([
  { key: 'has_unreviewed', label: 'Has unreviewed evaluations' },
  { key: 'any', label: 'Any' },
  { key: 'complete', label: 'Complete' },
  { key: 'not_started', label: 'Not started' },
]);

// The working queue (Tim, 2026-09-23): walk evaluations until every one has an explicit conclusion.
export const DEFAULT_EVALUATION_REVIEW_FILTER = 'has_unreviewed';
export const DEFAULT_COACH_ATTRIBUTION_FILTER = 'any';
export const DEFAULT_CLASS_AUDIT_FILTER = 'any';

export function evaluationReviewProgress(row = {}) {
  const total = count(row.evaluation_count);
  const reviewed = Math.min(count(row.evaluation_reviewed_count), total);
  const partly = Math.min(count(row.evaluation_partly_count), total - reviewed);
  let state;
  if (total === 0) state = EVALUATION_REVIEW_STATE.NO_EVALUATIONS;
  else if (reviewed === total) state = EVALUATION_REVIEW_STATE.COMPLETE;
  else if (reviewed === 0 && partly === 0) state = EVALUATION_REVIEW_STATE.NOT_STARTED;
  else state = EVALUATION_REVIEW_STATE.IN_PROGRESS;
  return { reviewed, partly, total, remaining: total - reviewed, state };
}

export function matchesEvaluationReviewFilter(row, key) {
  if (!key || key === 'any') return true;
  const { state } = evaluationReviewProgress(row);
  if (key === 'has_unreviewed') {
    return state === EVALUATION_REVIEW_STATE.NOT_STARTED || state === EVALUATION_REVIEW_STATE.IN_PROGRESS;
  }
  return state === key;
}

const count = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/**
 * Class audit progress for one identity row. The denominator is the CLASS-APPLICABLE evaluations
 * only: an evaluation with no Dash franchise (Sofive) has no Dash class to match, and a zero
 * denominator reads as not applicable rather than as "0 of 0 complete".
 */
export function classAuditProgress(row = {}) {
  const total = count(row.class_applicable_count);
  const done = Math.min(count(row.class_reconciled_count), total);
  let state;
  if (total === 0) state = CLASS_AUDIT_STATE.NOT_APPLICABLE;
  else if (done === 0) state = CLASS_AUDIT_STATE.NOT_STARTED;
  else if (done < total) state = CLASS_AUDIT_STATE.IN_PROGRESS;
  else state = CLASS_AUDIT_STATE.COMPLETE;
  return { done, total, state };
}

/**
 * Coach attribution progress for one identity row. Denominator = every evaluation in the identity's
 * review scope; numerator = those the warehouse no longer flags for identity review.
 *
 * `selections` is the number of coach choices recorded on class reconciliations. It is reported
 * beside the progress, never inside it: until the warehouse reads those choices they attribute
 * nothing.
 */
export function coachAttributionProgress(row = {}) {
  const total = count(row.evaluation_count);
  const resolved = Math.min(count(row.coach_resolved_count), total);
  const open = total - resolved;
  let state;
  if (total === 0) state = COACH_ATTRIBUTION_STATE.NO_EVALUATIONS;
  else if (open > 0) state = COACH_ATTRIBUTION_STATE.NEEDS_REVIEW;
  else state = COACH_ATTRIBUTION_STATE.ALL_RESOLVED;
  return { resolved, total, open, state, selections: count(row.coach_selection_count) };
}

export function matchesCoachAttributionFilter(row, key) {
  if (!key || key === 'any') return true;
  const { state } = coachAttributionProgress(row);
  if (key === 'needs_coach_review') return state === COACH_ATTRIBUTION_STATE.NEEDS_REVIEW;
  if (key === 'all_resolved') return state === COACH_ATTRIBUTION_STATE.ALL_RESOLVED;
  return true;
}

export function matchesClassAuditFilter(row, key) {
  if (!key || key === 'any') return true;
  const { state } = classAuditProgress(row);
  if (key === 'missing_class') {
    return state === CLASS_AUDIT_STATE.NOT_STARTED || state === CLASS_AUDIT_STATE.IN_PROGRESS;
  }
  return state === key;
}

// Per-evaluation flags from the API. Absent flags (an older response) are read as "in scope" and
// "class-applicable", which is what every row was before the flags existed.
const flagOn = (v) => v !== 0 && v !== false;

/** Whether one listed evaluation belongs to the identity's review (and therefore to its counts). */
export function isInReviewScope(evaluation = {}) {
  return flagOn(evaluation.in_review_scope);
}

/** Whether one listed evaluation has a Dash class recorded that counts toward Class audit. */
export function isClassRecorded(evaluation = {}) {
  return isInReviewScope(evaluation)
    && flagOn(evaluation.class_applicable)
    && Boolean(evaluation.is_reconciled)
    && evaluation.resolution_status === 'occurrence_confirmed';
}

/**
 * The grid-row counts, recomputed from an expanded evaluation list with the same rules the API's SQL
 * applies. Used so saving or removing a class updates that identity's grid row immediately rather
 * than on the next fetch. Coach counts come from the per-row warehouse flag, so they do not move on a
 * class save -- which is exactly the Class audit / Coach attribution separation.
 */
/**
 * One evaluation's review state, from its row: 'reviewed' (both judgments), 'partly' (class only) or
 * 'not_reviewed'. Uses the server's review_state when present; otherwise derives it the same way, so
 * an older API response (no review_state) still reads correctly.
 */
export function evaluationRowReviewState(e = {}) {
  if (e.review_state) return e.review_state;
  if (!e.is_reconciled) return 'not_reviewed';
  return e.coach_resolution || e.selected_employee_id ? 'reviewed' : 'partly';
}

export function evaluationProgressCounts(evaluations = []) {
  const scoped = evaluations.filter(isInReviewScope);
  const reconciled = scoped.filter((e) => Boolean(e.is_reconciled));
  // The class denominator follows the row's own outcome: "no Dash class expected" leaves it, exactly
  // as the server's CLASS_AUDIT_APPLICABLE rule does after a save.
  const classApplicable = scoped.filter((e) => flagOn(e.class_applicable) && e.resolution_status !== 'no_dash_occurrence_expected');
  return {
    evaluation_count: scoped.length,
    class_applicable_count: classApplicable.length,
    class_reconciled_count: scoped.filter(isClassRecorded).length,
    class_unresolved_count: classApplicable.filter((e) => Boolean(e.is_reconciled) && e.resolution_status === 'occurrence_unresolved').length,
    class_no_occurrence_count: reconciled.filter((e) => e.resolution_status === 'no_dash_occurrence_expected').length,
    coach_resolved_count: scoped.filter((e) => e.coach_resolved === 1 || e.coach_resolved === true).length,
    coach_selection_count: reconciled.filter((e) => e.selected_employee_id != null).length,
    coach_form_count: reconciled.filter((e) => e.coach_resolution === 'form_identity').length,
    evaluation_reviewed_count: scoped.filter((e) => evaluationRowReviewState(e) === 'reviewed').length,
    evaluation_partly_count: scoped.filter((e) => evaluationRowReviewState(e) === 'partly').length,
  };
}
